<template>
  <canvas class="pricechart__canvas" ref="pricechart" data-cy="canvas"></canvas>
</template>

<script setup lang="ts">
import Chart from 'chart.js'
import 'chartjs-adapter-date-fns'
import { useDate } from '@/composables/useDate.ts'
import { de } from 'date-fns/locale'
import { useMyCube } from '@/composables/useMyCube.js'
import { NumberUtil } from '@/util/NumberUtil'
import { ref, watch, onMounted, onBeforeUnmount, computed } from 'vue'
import type { HistoryDataState } from '@/types/HistoryData'
import { useChart, type ChartJS } from '@/composables/useChart';
import { useHistoryStore } from '@/stores/useHistoryStore'
import { storeToRefs } from 'pinia'
import { usePlans } from '@/composables/usePlans';

const { moment } = useDate()

const model = defineModel<HistoryDataState>()
const pricechart = ref<HTMLCanvasElement | null>(null)

const plans = usePlans()

const { setupEventHandlers } = useChart(pricechart)
const { hasProduction, hasConsumption } = useMyCube()
const { isFlexPlan, isFloatPlan, isFixPlan, showPastFlex, showPastFloat, showPastFix, historySelectedDate } = storeToRefs(useHistoryStore())

const chart = ref<ChartJS>(null)

function formatPrice2Fractions(value: number) {
  return `${(Math.round(value * 100) / 100).toLocaleString()} ct`
}

function formatPriceNoFractions(value: number) {
  return `${value.toLocaleString(undefined, {
    maximumFractionDigits: 0,
  })} ct`
}

function createStrompreisAktuellDataset() {
  return {
    label: 'Strompreis aktuell ct/kWh inkl.USt.*',
    fill: false,
    borderWidth: 2,
    lineTension: 0.8,
    barPercentage: 0.8,
    categoryPercentage: 1,
    cubicInterpolationMode: 'monotone',
    pointRadius: 0,
    data: [],
    borderJoinStyle: 'round',
    borderColor: '#1d8e85',
    pointStyle: 'none',
    yAxisID: 'yPrice',
    spanGaps: false,
  }
}

function createFlexpreisBezugDataset() {
  return {
    label: 'Flex-Preis Bezug ct/kWh inkl.USt.',
    fill: false,
    borderWidth: 2,
    lineTension: 0.8,
    barPercentage: 0.8,
    categoryPercentage: 1,
    cubicInterpolationMode: 'monotone',
    pointRadius: 0,
    data: [],
    borderJoinStyle: 'round',
    borderColor: 'rgba(180, 220, 255, 1)',
    pointStyle: 'none',
    yAxisID: 'yPrice',
    spanGaps: false,
    borderDash: [20, 5],
  }
}

function createFloatpreisBezugDataset() {
  return {
    label: 'Float-Preis Bezug ct/kWh inkl.USt.',
    fill: false,
    borderWidth: 2,
    lineTension: 0.8,
    barPercentage: 0.8,
    categoryPercentage: 1,
    cubicInterpolationMode: 'monotone',
    pointRadius: 0,
    data: [],
    borderJoinStyle: 'round',
    borderColor: 'rgba(180, 220, 255, 1)',
    pointStyle: 'none',
    yAxisID: 'yPrice',
    spanGaps: false,
    borderDash: [20, 5],
  }
}

function createFixpreisBezugDataset() {
  return {
    label: 'Fix-Preis Bezug ct/kWh inkl.USt.',
    fill: false,
    borderWidth: 2,
    lineTension: 0.8,
    barPercentage: 0.8,
    categoryPercentage: 1,
    cubicInterpolationMode: 'monotone',
    pointRadius: 0,
    data: [],
    borderJoinStyle: 'round',
    borderColor: 'rgba(180, 220, 255, 1)',
    pointStyle: 'none',
    yAxisID: 'yPrice',
    spanGaps: false,
    borderDash: [20, 5],
  }
}

function createStrompreisAktuellEinspeisungDataset() {
  return {
    label: 'Strompreis aktuell Einspeisung*',
    fill: false,
    borderWidth: 2,
    lineTension: 0.8,
    barPercentage: 0.8,
    categoryPercentage: 1,
    cubicInterpolationMode: 'monotone',
    pointRadius: 0,
    data: [],
    borderJoinStyle: 'round',
    borderColor: '#ff3d00',
    pointStyle: 'none',
    yAxisID: 'yPrice',
    spanGaps: false,
  }
}

function createFlexpreisEinspeisungDataset() {
  return {
    label: 'Flex-Preis Einspeisung',
    fill: false,
    borderWidth: 2,
    lineTension: 0.8,
    barPercentage: 0.8,
    categoryPercentage: 1,
    cubicInterpolationMode: 'monotone',
    pointRadius: 0,
    data: [],
    borderJoinStyle: 'round',
    borderColor: 'rgba(0, 0, 0, 0.18)',
    pointStyle: 'none',
    yAxisID: 'yPrice',
    spanGaps: false,
    borderDash: [20, 5],
  }
}

function createFloatpreisEinspeisungDataset() {
  return {
    label: 'Float-Preis Einspeisung',
    fill: false,
    borderWidth: 2,
    lineTension: 0.8,
    barPercentage: 0.8,
    categoryPercentage: 1,
    cubicInterpolationMode: 'monotone',
    pointRadius: 0,
    data: [],
    borderJoinStyle: 'round',
    borderColor: 'rgba(0, 0, 0, 0.18)',
    pointStyle: 'none',
    yAxisID: 'yPrice',
    spanGaps: false,
    borderDash: [20, 5],
  }
}

function createFixpreisEinspeisungDataset() {
  return {
    label: 'Fix-Preis Einspeisung',
    fill: false,
    borderWidth: 2,
    lineTension: 0.8,
    barPercentage: 0.8,
    categoryPercentage: 1,
    cubicInterpolationMode: 'monotone',
    pointRadius: 0,
    data: [],
    borderJoinStyle: 'round',
    borderColor: 'rgba(0, 0, 0, 0.18)',
    pointStyle: 'none',
    yAxisID: 'yPrice',
    spanGaps: false,
    borderDash: [20, 5],
  }
}

const config: any = {
  type: 'line',
  options: {
    maintainAspectRatio: false,
    fontColor: '#dddddd',
    legend: {
      position: 'bottom',
    },
    scales: {
      xAxes: [
        {
          categorySpacing: 1,
          stacked: true,
          gridLines: {
            color: 'rgba(0, 0, 0, 0.03)',
            zeroLineColor: 'transparent',
          },
          display: true,
          ticks: {
            autoSkip: true,
            autoSkipPadding: 5,
          },
          type: 'time',
          time: {
            displayFormats: {
              day: 'E dd.MM',
              hour: 'HH:mm',
            },
          },
          adapters: {
            date: {
              locale: de,
            },
          },
        },
      ],
      yAxes: [
        {
          id: 'yPrice',
          stacked: false,
          position: 'left',
          ticks: {
            suggestedMin: 0,
            suggestedMax: 1,
            beginAtZero: true,
            fontColor: '#888',
            callback: (value: number) => formatPriceNoFractions(value),
          },
          type: 'linear',
        },
      ],
    },
    hover: {
      mode: 'index',
      intersect: false,
    },
    tooltips: {
      mode: 'index',
      intersect: false,
      callbacks: {
        title: (item: any, data: any) => {
          return moment(data.labels[item[0].index]).format('DD.MM HH:mm')
        },
        label: (item: any, data: any) => {
          const dataset = data.datasets[item.datasetIndex]
          const value = item.yLabel + ''
          return dataset.label + ': ' + formatPrice2Fractions(Number(value))
        },
      },
    },
  },
  data: {
    labels: [],
    datasets: [] as any[],
  },
}

if (isFlexPlan.value) {
  if (hasConsumption.value) {
    config.data.datasets.push(
      createStrompreisAktuellDataset(),
      createFlexpreisBezugDataset()
    )
  }

  if (hasProduction.value) {
    config.data.datasets.push(
      createStrompreisAktuellEinspeisungDataset(),
      createFlexpreisEinspeisungDataset()
    )
  }
}

if (isFloatPlan.value) {
  if (hasConsumption.value) {
    config.data.datasets.push(
      createStrompreisAktuellDataset(),
      createFloatpreisBezugDataset()
    )
  }

  if (hasProduction.value) {
    config.data.datasets.push(
      createStrompreisAktuellEinspeisungDataset(),
      createFloatpreisEinspeisungDataset()
    )
  }
}

if (isFixPlan.value) {
  if (hasConsumption.value) {
    config.data.datasets.push(
      createStrompreisAktuellDataset(),
      createFixpreisBezugDataset()
    )
  }

  if (hasProduction.value && plans.hasGenerationPlanStandardPrice()) {
    config.data.datasets.push(
      createStrompreisAktuellEinspeisungDataset(),
      createFixpreisEinspeisungDataset()
    )
  }
}

function getDisplayedDataset(data: HistoryDataState, label: string) {
  let dataset = [];

  if (label === 'Flex-Preis Bezug ct/kWh inkl.USt.') {
    dataset = showPastFlex.value ? data.datasets.baseFrom : []
  }

  if (label === 'Float-Preis Bezug ct/kWh inkl.USt.') {
    dataset =  showPastFloat.value ? data.datasets.baseFrom : []
  }

  if (label === 'Fix-Preis Bezug ct/kWh inkl.USt.') {
    dataset =  showPastFix.value ? data.datasets.baseFrom : []
  }

  if (label === 'Flex-Preis Einspeisung') {
    dataset =  showPastFlex.value && hasProduction.value
      ? data.datasets.baseTo?.map((p: number) => -p)
      : []
  }

  if (label === 'Float-Preis Einspeisung') {
    dataset =  showPastFloat.value && hasProduction.value
      ? data.datasets.baseTo?.map((p: number) => -p)
      : []
  }

  if (label === 'Fix-Preis Einspeisung') {
    dataset =  showPastFix.value && hasProduction.value
      ? data.datasets.baseTo?.map((p: number) => -p)
      : []
  }

  if (label === 'Strompreis aktuell ct/kWh inkl.USt.*') {
    const isToday = moment()
      .startOf('day')
      .isSame(historySelectedDate.value)

    dataset = isToday ? data.datasets.pastPrice ?? [] : []
  }

  if (label === 'Strompreis aktuell Einspeisung*') {
    const isToday = moment()
      .startOf('day')
      .isSame(historySelectedDate.value)

    dataset =  isToday && hasProduction.value
      ? data.datasets?.pastPriceProduction
      : []
  }

  // dataset = dataset?.map(entry => !entry ? 0 : entry) ?? []

  return dataset ?? []
}

function updateChart(data: HistoryDataState) {
  if (data.labels.length < 1) {
    return
  }

  const chartSettings = { ...config }

  chartSettings.data.labels = data.labels

  chartSettings.data.datasets.forEach((elem: any) => {
    elem.data = getDisplayedDataset(data, elem.label)
  })

  const maxValue = NumberUtil.max(
    ...(data.datasets.baseFrom ?? []),
    ...(data.datasets.pastPrice ?? [])
  )

  const minValue = hasProduction
    ? NumberUtil.min(
        ...(data.datasets.baseTo ?? []),
        ...(data.datasets.pastPriceProduction ?? [])
      )
    : 0

  const furthest = NumberUtil.max(maxValue ?? 0, Math.abs(minValue ?? 0))

  chartSettings.options.scales.yAxes.forEach((axis: any) => {
    axis.ticks.suggestedMax = furthest
    axis.ticks.suggestedMin = 0
  })

  if (!chart.value) {
    chart.value = new Chart(pricechart.value as HTMLCanvasElement, chartSettings) as ChartJS
    setupEventHandlers(chart.value, pricechart.value)
  } else {
    chart.value.data = chartSettings.data
    chart.value.options = chartSettings.options
  }

  chart.value.update()
}

function showLegend() {
  return window.innerHeight >= 500
}

function onResize() {
  if (chart.value) {
    chart.value.options.legend.display = showLegend()
  }
}

onMounted(() => {
  window.addEventListener('resize', onResize)

  if (model.value) {
    updateChart(model.value)
  }
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', onResize)
})

watch(() => model.value, (data: HistoryDataState) => updateChart(data))
</script>
–
